import { useCallback, useContext, useState } from "react"
import { useAppDispatch, useAppSelector } from "../../../../app/hooks"
import { SectionDeleteContext } from "../../DocumentContent"
import { isDocEditable, sectionSelectors, selectDocument } from "../../documentSlice"
import { impossible } from "../../../../app/Utils"
import { APIError } from "../../../../app/appTypes"
import { updateDocSection } from "../../documentsApi"
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, IconButton, Skeleton, TextField, Typography } from "@mui/material"
import { ServerErrorMsg } from "../../../../app/ServerErrorMsg"
import { Localized } from "@fluent/react"
import { If } from "../../../../app/If"
import { LoadingButton } from "@mui/lab"
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import { showError } from "../../../notifications/notificationsSlice"
import { FormSectionView } from "./FormSectionView"
import { selectFormSection } from "./formSectionSlice"
import { FormFieldValue } from "./formSchema"
import { loadDocumentForms, setFormFieldValue } from "./formSectionApi"
import { useDocumentForms } from "./formSectionLoaders"

export const FormSectionWrapper = (props: { sectionId: string }) => {
    const { sectionId } = props
    const dispatch = useAppDispatch()
    const deleteSection = useContext(SectionDeleteContext)
    const section = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const header = useAppSelector(selectDocument).doc?.header ?? impossible()
    const isEditable = useAppSelector(isDocEditable)
    const formsState = useDocumentForms(header.id)
    const form = useAppSelector(state => selectFormSection(state, sectionId))

    const { numeration } = section

    const [isEditingTitle, setIsEditingTitle] = useState(false);
    const [tempTitle, setTempTitle] = useState(section.name);
    const [isHovered, setIsHovered] = useState(false);
    const [error, setError] = useState<APIError | null>(null)
    const [del, setDel] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const documentId = header.id
    // Optimistic: the store already holds the value. On failure, reload so the form shows what the
    // server really has.
    const commitValue = useCallback(
        (key: string, value: FormFieldValue) => {
            dispatch(setFormFieldValue({ documentId, sectionId, key, value }))
                .unwrap()
                .catch(() => {
                    dispatch(showError('error'))
                    dispatch(loadDocumentForms(documentId))
                })
        },
        [dispatch, documentId, sectionId],
    )

    const handleTitleClick = () => {
        setIsEditingTitle(true);
        setTempTitle(section.name);
    };

    const handleApprove = async () => {
        if (tempTitle) {
            try {
                await dispatch(updateDocSection({ ...section, name: tempTitle })).unwrap()
                setIsEditingTitle(false);
            } catch (error) {
                setError(error as APIError)
            }
        }
    };

    const handleConfirmDeleteClick = async () => {
        try {
            setDeleting(true)
            deleteSection(sectionId)
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setDeleting(false)
        }
    }

    return (
        <>
            <Box display="flex" alignItems="center" gap={1} onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)}>
                <ServerErrorMsg err={error} />
                <Typography variant="h5" color="primary">
                    {numeration}.
                </Typography>
                {isEditingTitle ? (
                    <>
                        <TextField
                            value={tempTitle}
                            onChange={e => setTempTitle(e.target.value)}
                            autoFocus
                            variant="standard"
                            fullWidth
                        />
                        <IconButton color="success" onClick={handleApprove}>
                            <CheckIcon />
                        </IconButton>
                        <IconButton color="error" onClick={() => setIsEditingTitle(false)}>
                            <CloseIcon />
                        </IconButton>
                    </>
                ) : (
                    <Box display="flex" alignItems="center" gap={1} sx={{ cursor: 'pointer' }} onClick={handleTitleClick}>
                        <Typography
                            variant="h5"
                            color="primary"
                            sx={{
                                transition: "0.2s",
                                ...(isHovered && { textDecoration: "underline", color: "secondary.main" })
                            }}
                        >
                            {section.name}
                        </Typography>
                        {isHovered && (
                            <IconButton size="small" color="primary">
                                <EditIcon fontSize="small" />
                            </IconButton>
                        )}
                    </Box>
                )}
                {form && <Chip size="small" variant="outlined" label={`${form.definitionCode} v${form.definitionVersion}`} />}
                {isEditable && <Button onClick={() => setDel(true)} color='error' variant='outlined' startIcon={<DeleteIcon />} sx={{ ml: 'auto' }} size='small'>
                    <Localized id='delete'>Usuń</Localized>
                </Button>}
            </Box>
            <Box sx={{ mt: 2 }}>
                {form
                    ? <FormSectionView
                        fields={form.fields}
                        values={form.values}
                        language={header.language}
                        readOnly={!isEditable}
                        onCommit={commitValue} />
                    : formsState === 'loaded'
                        // The section exists but its form does not: created before forms were stored.
                        ? <Alert severity="warning">
                            <Localized id="form-section-no-data">
                                <span>_Ta sekcja nie ma danych formularza. Usuń ją i dodaj ponownie.</span>
                            </Localized>
                        </Alert>
                        : formsState === 'error'
                            ? <Alert severity="error" action={<Button color="inherit" size="small" onClick={() => dispatch(loadDocumentForms(documentId))}>
                                <Localized id="try-again"><span>_Spróbuj ponownie</span></Localized>
                            </Button>}>
                                <Localized id="form-section-load-error"><span>_Nie udało się wczytać formularzy</span></Localized>
                            </Alert>
                            : <Skeleton variant="rectangular" height={120} />}
            </Box>
            <Dialog open={del}>
                <DialogContent sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                }}>
                    <If condition={section.subSectionsIds.length > 0}>
                        <Alert severity='warning' variant='standard'>
                            <Localized id='document-delete-section-warning'>_Podsekcje zostaną również usunięte</Localized>
                        </Alert>
                    </If>
                    <DialogContentText>
                        <Localized id="confirm-delete">
                            <span>_Czy napewno chcesz usunąć?</span>
                        </Localized>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <LoadingButton loading={deleting} onClick={handleConfirmDeleteClick}>
                        <Localized id="yes"><span>_Tak</span></Localized>
                    </LoadingButton>
                    <LoadingButton loading={deleting} onClick={() => setDel(false)}>
                        <Localized id="no"><span>_Nie</span></Localized>
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </>
    );
}
